/**
 * AttributeValuesList - List component for displaying and filtering attribute values
 */
import { ReactElement, useState, useMemo, useEffect } from 'react'
import { connect } from 'react-redux'
import { List, Flex, Spin, Input } from 'antd'
import { fetchAttributeValues } from 'actions/search/attributes'
import {
  getAttributeValues,
  isFetchingAttributeValues,
} from 'selectors/search/attributes'
import {
  addAttributeValueFilter,
  removeAttributeValueFilter,
} from 'actions/search/filters'
import {
  getAttributeValueFilters,
  getTimeRange,
  getCustomDateRange,
} from 'selectors/search/filters'
import {
  getSelectedAttributeCode,
  getManualSearchAt,
} from 'selectors/search/ui'
import type { AttributeValueCount } from 'types/search/attributes'
import type { TimeRange } from 'types/search/filters'
import styles from './AttributeValuesList.module.scss'

interface AttributeValuesListProps {
  attributeValues: AttributeValueCount[]
  selectedValues: string[]
  appliedFilters: Array<{ attributeCode: string; value: string }>
  isFetching: boolean
  selectedAttributeCode: string | null
  timeRange: TimeRange
  customDateRange: { startDate: string | null; endDate: string | null }
  manualSearchAt: string | null
}

interface ReduxState {
  attributeValues: AttributeValueCount[]
  selectedValues: string[]
  appliedFilters: Array<{ attributeCode: string; value: string }>
  isFetching: boolean
  selectedAttributeCode: string | null
  timeRange: TimeRange
  customDateRange: { startDate: string | null; endDate: string | null }
  manualSearchAt: string | null
}

const select = (): ReduxState => {
  const selectedAttributeCode = getSelectedAttributeCode()
  const appliedFilters = getAttributeValueFilters()

  // Get selected values for current attribute
  const selectedValues = selectedAttributeCode
    ? appliedFilters
        .filter((filter) => filter.attributeCode === selectedAttributeCode)
        .map((filter) => filter.value)
    : []

  return {
    attributeValues: getAttributeValues(),
    selectedValues,
    appliedFilters,
    isFetching: isFetchingAttributeValues(),
    selectedAttributeCode,
    timeRange: getTimeRange(),
    customDateRange: getCustomDateRange(),
    manualSearchAt: getManualSearchAt(),
  }
}

/**
 * AttributeValuesList - Displays attribute values with counts and local filtering
 * Connected to Redux for autonomous data management
 */
const AttributeValuesList = (props: AttributeValuesListProps): ReactElement => {
  const {
    attributeValues,
    selectedValues,
    appliedFilters,
    isFetching,
    selectedAttributeCode,
    timeRange,
    customDateRange,
    manualSearchAt,
  } = props

  const [valueFilter, setValueFilter] = useState<string>('')

  // Fetch attribute values when selected attribute or filters change
  useEffect(() => {
    if (selectedAttributeCode) {
      fetchAttributeValues(selectedAttributeCode)
    }
  }, [
    selectedAttributeCode,
    timeRange,
    customDateRange,
    manualSearchAt,
    appliedFilters,
  ])

  // Filter attribute values based on search text
  const filteredAttributeValues = useMemo(() => {
    if (!valueFilter.trim()) {
      return attributeValues
    }

    const filterText = valueFilter.toLowerCase()
    return attributeValues.filter((item) =>
      item.attributeValue.toLowerCase().includes(filterText),
    )
  }, [attributeValues, valueFilter])

  return (
    <Flex vertical className={styles.container}>
      <Input
        placeholder="Filter values..."
        allowClear
        value={valueFilter}
        onChange={(e) => setValueFilter(e.target.value)}
        className={styles.filter}
      />
      <div className={styles.scrollable}>
        {isFetching ? (
          <Flex
            justify="center"
            align="center"
            className={styles.loadingContainer}
          >
            <Spin tip="Loading values..." />
          </Flex>
        ) : (
          <List
            dataSource={[...filteredAttributeValues].sort(
              (a, b) => b.count - a.count,
            )}
            renderItem={(item) => {
              const isSelected = selectedValues.includes(item.attributeValue)
              return (
                <List.Item
                  key={item.attributeValue}
                  className={
                    isSelected ? styles.listItemSelected : styles.listItem
                  }
                  onClick={() => {
                    if (!selectedAttributeCode) return

                    if (isSelected) {
                      removeAttributeValueFilter(
                        selectedAttributeCode,
                        item.attributeValue,
                      )
                    } else {
                      addAttributeValueFilter(
                        selectedAttributeCode,
                        item.attributeValue,
                      )
                    }
                  }}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    className={styles.itemContent}
                  >
                    <span
                      title={item.attributeValue}
                      className={styles.valueText}
                    >
                      {item.attributeValue}
                    </span>
                    {item.count}
                  </Flex>
                </List.Item>
              )
            }}
          />
        )}
      </div>
    </Flex>
  )
}

export default connect(select)(AttributeValuesList)
